import { Injectable } from '@angular/core';
import { fromJS, List } from 'immutable';
import { isEmpty, isNumber } from 'lodash';
import { Document } from '../document-data/document.model';
import { PaymentTransaction } from '../document-data/sub-models/payment-transaction.model';
import { IRendererOptions } from '../renderer/renderer.interface';
import {
  CompanySettings,
  ManualPaymentReceiptsEnabledEnum,
} from '../settings-data/sub-models/company-settings/company-settings.model';
import { SettingsContent } from '../settings-data/sub-models/settings-content.model';
import {
  IAddPaymentPayload,
  IBankTransferPayload,
  ICardPaymentsPayload,
  IPaypalPayload,
  IReceiptPreviewPayload,
  IRemindersPreview,
  ISendReceiptPayload,
} from './payments-data.data';
import { PayPalEcStatesEnum } from 'i2g-ng-payment';
import { I2gNgTrackingActionBuilder, ITrackingAction } from 'i2g-ng-tracking';

@Injectable()
export class PaymentsDataBuilder {
  constructor(private _trackingActionBuilder: I2gNgTrackingActionBuilder) {}

  buildBankTransferPayload(enabled: boolean): IBankTransferPayload {
    return {
      enabled,
    };
  }

  buildCardPaymentsPayload(
    enabled: boolean,
    countryCode: string
  ): ICardPaymentsPayload {
    return {
      payments_enabled_by_user: enabled,
      country_code: countryCode,
    };
  }

  buildPaypalPayload(enabled: boolean): IPaypalPayload {
    return {
      paypal_ec_enabled: enabled,
    };
  }

  buildRemindersPreviewPayload(schedule: List<number>): IRemindersPreview {
    return {
      reminder_days: schedule.toJS(),
    };
  }

  buildSendReceiptPayload(
    documentId: string,
    transactionId: string,
    renderingOptions: IRendererOptions
  ): ISendReceiptPayload {
    return {
      document_id: documentId,
      transaction_id: transactionId,
      rendering_options: renderingOptions,
    };
  }

  buildReceiptPreviewPayload(
    document: Document,
    transaction: IAddPaymentPayload,
    rendererOptions: IRendererOptions
  ): IReceiptPreviewPayload {
    const transactionItem = new PaymentTransaction(transaction)
      .transactionItemPayload()
      .map((value) => (!isNumber(value) && isEmpty(value) ? null : value)); // replacing not accepted empty values by null
    // for backend validation
    return {
      transaction_item: transactionItem.toJS(),
      document: document.toJS(),
      rendering_options: rendererOptions,
    };
  }

  buildPaymentReceiptsSettingsProps(toggleValue: boolean) {
    const manualPaymentReceiptsEnabled = toggleValue
      ? ManualPaymentReceiptsEnabledEnum.on
      : ManualPaymentReceiptsEnabledEnum.off;

    return fromJS({
      [SettingsContent.KEYS.COMPANY_SETTINGS]: {
        [CompanySettings.KEYS
          .MANUAL_PAYMENT_RECEIPTS_ENABLED]: manualPaymentReceiptsEnabled,
      },
    });
  }

  buildPayPalWarningTracking(
    status: PayPalEcStatesEnum,
    trackingScreenName: string
  ): ITrackingAction {
    const statusToButtonIdentifierMap = {
      [PayPalEcStatesEnum.in_progress]: 'paypal_ec_resume',
      [PayPalEcStatesEnum.email_not_confirmed]:
        'paypal_ec_learn_more_verification',
      [PayPalEcStatesEnum.restricted]: 'paypal_ec_learn_more_restricted',
    };

    return this._trackingActionBuilder.buildButtonTapped(
      statusToButtonIdentifierMap[status],
      trackingScreenName
    );
  }
}
